// sphinx vocabulary + route input schemas. Shared by server routes and client.
import { z } from "zod";

export const TARGET_IDS = [
  "claude-desktop",
  "claude-cowork",
  "claude-code",
  "claude-code-desktop",
  "claude-web",
  "chatgpt-desktop",
  "chatgpt-web",
  "codex-desktop",
  "anthropic-api",
  "openai-api",
] as const;
export type TargetId = (typeof TARGET_IDS)[number];

export const SPHINX_MODELS = [
  "claude-opus-5",
  "claude-fable-5",
  "gpt-5.6-luna",
  "gpt-5.6-sol",
] as const;
export type SphinxModel = (typeof SPHINX_MODELS)[number];

export const YARN_ENVS = ["dev", "prod"] as const;
export type YarnEnv = (typeof YARN_ENVS)[number];

/**
 * A connector identity is (env, org): each Yarn organization is its own connector because
 * the connector's auth IS an org's key. Connector/plugin display names follow
 * `yarn-<env>-<org>` (e.g. yarn-dev-clay); server rows, API keys
 * (SPHINX_YARN_API_KEY_<ENV>_<ORG>) and server-truth org ids (SPHINX_YARN_ORG_ID_<ENV>_<ORG>)
 * are keyed the same way. `harness` is the default org.
 */
export const YARN_ORGS = ["harness", "clay", "faire", "front"] as const;
export type YarnOrg = (typeof YARN_ORGS)[number];
export const DEFAULT_YARN_ORG: YarnOrg = "harness";
export const yarnOrgSchema = z.enum(YARN_ORGS);
export const connectorName = (env: YarnEnv, org: YarnOrg): string => `yarn-${env}-${org}`;

/**
 * Names of every seeded connector row (`server/db/seed.ts`): the three prewired
 * third-party/default rows plus the per-(env, org) Yarn connectors. These rows
 * are the product's fixed matrix — the delete route refuses them (409) and the
 * Connectors UI hides Remove for them; the seed re-inserts any that go missing.
 */
export const SEEDED_CONNECTOR_NAMES: ReadonlySet<string> = new Set(
  (["harness", "clay", "faire", "front"] as const).map((org) => connectorName("dev", org))
);

export const RUN_STATUSES = [
  "pending",
  "running",
  "complete",
  "canceled",
  "failed",
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

/**
 * What broke when an iteration terminally fails: the environment (connector
 * unreachable, workspace not ready), the infrastructure (timeouts, leases,
 * orphans), or the model itself (matcher/judge failed a real attempt).
 * Derived from the error string by `server/queue/failure-kind.ts` — the
 * 2026-08-25 y-dev-api outage (run 2667b8b1) graded 14 env failures as
 * model failures, one judge calling the model a fabricator.
 */
export const FAILURE_KINDS = ["env", "infra", "model"] as const;
export type FailureKind = (typeof FAILURE_KINDS)[number];

// Iterations share the run status vocabulary.
export const ITERATION_STATUSES = RUN_STATUSES;
export type IterationStatus = RunStatus;

export const targetIdSchema = z.enum(TARGET_IDS);
export const sphinxModelSchema = z.enum(SPHINX_MODELS);
export const yarnEnvSchema = z.enum(YARN_ENVS);
export const runStatusSchema = z.enum(RUN_STATUSES);

const jsonObject = z.record(z.string(), z.unknown());
const jsonValue = z.unknown().refine((v) => v !== undefined, "required");

// ---- servers ----
export const createServerSchema = z.object({
  name: z.string().min(1),
  config: jsonObject,
});
export const updateServerSchema = z
  .object({ name: z.string().min(1), config: jsonObject })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "empty patch");

// ---- suites / cases ----
export const createSuiteSchema = z.object({
  name: z.string().min(1),
  yaml: z.string().optional(),
});
export const updateSuiteSchema = z
  .object({ name: z.string().min(1), yaml: z.string() })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "empty patch");

export const createCaseSchema = z.object({ json: jsonValue });
export const updateCaseSchema = z
  .object({ json: jsonValue, ord: z.number().int().min(0) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "empty patch");

// ---- runs / iterations ----
export const createRunSchema = z.object({
  targets: z.array(targetIdSchema).min(1),
  yarnEnv: yarnEnvSchema,
  yarnOrg: yarnOrgSchema.default(DEFAULT_YARN_ORG),
  note: z.string().optional(),
  /** Restrict the run to these cases (quick run); omitted = every case in the suite. */
  caseIds: z.array(z.string().min(1)).min(1).optional(),
});

// ---- playground ----
/**
 * Name of the hidden system suite (`suites.kind = 'system'`) the Playground
 * dispatches into: one case per message sent to real clients, one run per
 * send. The Evaluate suite list shows `kind = 'user'` only.
 */
export const PLAYGROUND_SUITE_NAME = "__playground__";
/** `POST /playground/runs` — a chat message run against real clients through the queue. */
export const createPlaygroundRunSchema = z.object({
  prompt: z.string().min(1),
  targets: z.array(targetIdSchema).min(1),
  /**
   * Explicit connector override for this send. Omitted = each client runs with
   * its own attached set (`targets.server_ids` — the card's "Used in <client>
   * runs"); present (even empty) = exactly these for every client. The run's
   * yarn connector is always attached either way.
   */
  serverIds: z.array(z.string().min(1)).optional(),
  yarnEnv: yarnEnvSchema.default("dev"),
  yarnOrg: yarnOrgSchema.default(DEFAULT_YARN_ORG),
  /**
   * Inline model picks, one per client (`{ "claude-desktop": "claude-opus-5" }`).
   * Each overrides that client's configured model for this run; a client with no
   * entry keeps its own. A pick whose provider family does not match the client
   * is ignored (a Claude client cannot run a GPT model) — see `modelOverridesFor`.
   */
  models: z.partialRecord(targetIdSchema, sphinxModelSchema).optional(),
  /**
   * The Playground conversation this send belongs to. The dispatch upserts
   * the session row so the conversation shows in the Sessions rail the
   * moment the runs are fired — and records the lineup below. The prompt is
   * appended to the transcript only when the lineup has no direct lane
   * (otherwise chat-v2's own persist owns the transcript and a second
   * writer would double-append it).
   */
  chatSessionId: z.string().min(1).optional(),
  /** The full Playground lineup for this send: "direct" and/or target ids. */
  lineup: z.array(z.string().min(1)).max(16).optional(),
});

/**
 * Keep only the model picks a client can actually run: a Claude client cannot
 * run a GPT model, so a mismatched pick is dropped (the client keeps its own
 * configured model) instead of producing an iteration that claims otherwise.
 */
export function modelOverridesFor(
  targets: readonly TargetId[],
  models: Partial<Record<TargetId, SphinxModel>> | undefined
): Partial<Record<TargetId, SphinxModel>> {
  if (!models) return {};
  const out: Partial<Record<TargetId, SphinxModel>> = {};
  for (const target of targets) {
    const model = models[target];
    if (model && MODEL_PROVIDER[model] === TARGET_PROVIDER[target]) out[target] = model;
  }
  return out;
}

const isoDate = z.string().datetime({ offset: true });
export const updateIterationSchema = z
  .object({
    status: runStatusSchema,
    startedAt: isoDate.nullable(),
    finishedAt: isoDate.nullable(),
    clientTranscript: jsonValue.nullable(),
    serverTruth: jsonValue.nullable(),
    grade: jsonValue.nullable(),
    artifactPrefix: z.string().nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "empty patch");

// ---- targets ----
export const updateTargetSchema = z
  .object({
    enabled: z.boolean(),
    model: sphinxModelSchema.nullable(),
    /**
     * Every model this client runs. A run expands to case × client × model, so
     * two models here means two iterations per case for this client. Empty =
     * fall back to `model`, and then to the provider default.
     */
    models: z.array(sphinxModelSchema),
    serverIds: z.array(z.string().min(1)),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "empty patch");

// ---- chat sessions ----
export const createChatSessionSchema = z.object({
  title: z.string().nullable().optional(),
  json: jsonValue,
});
export const updateChatSessionSchema = z
  .object({
    title: z.string().nullable(),
    json: jsonValue,
    /** Playground client lineup ("direct" + target ids) — see migration 015. */
    lineup: z.array(z.string().min(1)).max(16).nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, "empty patch");

// ---- events ----
export const appendEventSchema = z.object({
  iterationId: z.string().min(1).optional(),
  json: jsonValue,
});

// ---- job queue ----
export const JOB_STATES = [
  "queued",
  "assigned",
  "running",
  "succeeded",
  "failed",
  "canceled",
  "orphaned",
] as const;
export type JobState = (typeof JOB_STATES)[number];

// server: runs in the intermediary's local worker pool (direct APIs, CLIs).
// mac-cdp: a Mac runner drives an Electron/web app over CDP (concurrent-capable).
// mac-exclusive: screen-level driving; one per Mac.
export const JOB_CLASSES = ["server", "mac-cdp", "mac-exclusive"] as const;
export type JobClass = (typeof JOB_CLASSES)[number];

// Probe 2026-08-22 (ops/probe-electron-ports.md): Claude Desktop refuses to
// start with any debugging switch (Cowork is a mode inside it) → vision path,
// one per Mac. ChatGPT Desktop is Chromium and honors --remote-debugging-port
// → CDP.
export const TARGET_CLASS: Record<TargetId, JobClass> = {
  "anthropic-api": "server",
  "openai-api": "server",
  "claude-code": "server",
  "claude-web": "mac-cdp",
  "chatgpt-web": "mac-cdp",
  "chatgpt-desktop": "mac-cdp",
  "codex-desktop": "mac-cdp",
  "claude-desktop": "mac-exclusive",
  "claude-cowork": "mac-exclusive",
  "claude-code-desktop": "mac-exclusive",
};

export const TARGET_PROVIDER: Record<TargetId, "anthropic" | "openai"> = {
  "claude-desktop": "anthropic",
  "claude-cowork": "anthropic",
  "claude-code": "anthropic",
  "claude-code-desktop": "anthropic",
  "claude-web": "anthropic",
  "anthropic-api": "anthropic",
  "chatgpt-desktop": "openai",
  "chatgpt-web": "openai",
  "codex-desktop": "openai",
  "openai-api": "openai",
};

/** Which provider family each model belongs to (a Claude client cannot run a GPT model). */
export const MODEL_PROVIDER = {
  "claude-opus-5": "anthropic",
  "claude-fable-5": "anthropic",
  "gpt-5.6-luna": "openai",
  "gpt-5.6-sol": "openai",
} as const satisfies Record<SphinxModel, "anthropic" | "openai">;

export const DEFAULT_MODEL_FOR_PROVIDER = {
  anthropic: "claude-opus-5",
  openai: "gpt-5.6-sol",
} as const satisfies Record<"anthropic" | "openai", SphinxModel>;

/**
 * One deterministic MCP tool call a case declares around an iteration
 * (`setup`/`teardown`). Run SERVER-SIDE against the job's own Yarn connector
 * (queue/fixtures.ts) as client "sphinx-fixture", which server-truth
 * correlation drops — fixture traffic never enters the graded record.
 */
export const fixtureCallSchema = z.object({
  toolName: z.string().min(1),
  arguments: z.record(z.string(), z.unknown()).optional(),
});
export type FixtureCall = z.infer<typeof fixtureCallSchema>;

/** The case fields an executor + grader need, normalized from cases.json. */
export const caseSpecSchema = z.object({
  caseId: z.string().min(1),
  title: z.string(),
  prompt: z.string(),
  expectedToolCalls: z
    .array(
      z.object({
        toolName: z.string().min(1),
        arguments: z.record(z.string(), z.unknown()).optional(),
      })
    )
    .default([]),
  isNegativeTest: z.boolean().default(false),
  expectedOutput: z.string().optional(),
  judgeRubric: z.string().optional(),
  // Optional (no default): a case without fixtures freezes byte-identical to
  // before these fields existed. Setup may re-run on requeue (orphan retry,
  // deploy drain) with the same stamp — setup tools must tolerate that; the
  // post-run golden restore sweeps any duplicates.
  setup: z.array(fixtureCallSchema).optional(),
  teardown: z.array(fixtureCallSchema).optional(),
  // Per-case job budget override (seconds). The class default (1800) walls the
  // dispatch-and-wait cases: run a52bd8b8's make-a-new-video iteration aborted
  // at exactly 1800.077s while check_run long-polled a legitimate render.
  timeoutSec: z.number().int().min(30).max(3600).optional(),
  // Set by applyStampToCase (queue/stamp.ts) when `{{stamp}}` substitution
  // actually changed the case: the iteration's mutation arguments embed its
  // own stamp, so grading can attribute server-truth calls under overlap
  // (grading/correlate.ts) and the mac claim gate may run two stamped cases
  // concurrently on one (env, org) (queue/mac-claim.ts). Absent on unstamped
  // cases — their spec_json stays byte-identical to before this field existed.
  stamped: z.boolean().optional(),
});
export type CaseSpec = z.infer<typeof caseSpecSchema>;

/** An MCP server resolved for a job; auth token is env-derived, never stored in cases. */
export const jobServerSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  url: z.string().url(),
  authToken: z.string().optional(),
});
export type JobServer = z.infer<typeof jobServerSchema>;

export const jobSpecSchema = z.object({
  jobId: z.string().min(1),
  iterationId: z.string().min(1),
  runId: z.string().min(1),
  target: targetIdSchema,
  model: sphinxModelSchema,
  yarnEnv: yarnEnvSchema,
  yarnOrg: yarnOrgSchema.default(DEFAULT_YARN_ORG),
  servers: z.array(jobServerSchema),
  case: caseSpecSchema,
  timeoutSec: z.number().int().min(30).max(3600),
});
export type JobSpec = z.infer<typeof jobSpecSchema>;

// ---- transcripts / grades ----
export interface ObservedToolCall {
  name: string;
  args?: unknown;
  resultSummary?: string;
  isError?: boolean;
  /** Which MCP server served the call (connector name) — a multi-connector run
   * calls the same tool once per org, and unlabeled they read as duplicates. */
  server?: string;
}
export interface TranscriptTurn {
  prompt: string;
  responseText: string;
  observedToolCalls: ObservedToolCall[];
}
/** exact: read from an API/CLI response; scraped: observed from a driven UI. */
export interface ClientTranscript {
  turns: TranscriptTurn[];
  exactness: "exact" | "scraped";
  raw?: unknown;
}

export interface MatcherResult {
  passed: boolean;
  expected: string[];
  observed: string[];
  missing: string[];
  unexpected: string[];
  argMismatches: string[];
  outputChecks: { kind: string; passed: boolean; detail: string }[];
}
export const judgeVerdictSchema = z.object({
  verdict: z.enum(["pass", "fail", "unproven"]),
  score: z.number().min(0).max(1),
  reasons: z.array(z.string()),
});
export type JudgeVerdict = z.infer<typeof judgeVerdictSchema>;
/** matcher is null when the iteration failed before producing a transcript. */
/** One post-run read of a project a video-agent run touched (grading/outcome.ts). */
export interface OutcomeRead {
  projectId: string;
  runId?: string;
  /** check_job (kind 'run') status at read time (check_run on a pre-consolidation connector); a run not 'succeeded' may not have applied its edits. */
  runStatus?: string;
  /** describe_project view=script JSON, clamped — what the video actually says post-run. */
  script?: string;
  error?: string;
}
/** Post-run workspace state for the projects this iteration's own dispatches touched. */
export interface OutcomeReport {
  readAt: string;
  /** How long the grader waited for the dispatched runs to reach a terminal status. */
  waitedMs: number;
  reads: OutcomeRead[];
}

export interface Grade {
  passed: boolean;
  /**
   * The case asserted nothing — no `expectedToolCalls`, no `expectedOutput`, no
   * `judgeRubric` — so the matcher passed vacuously and `passed` means only "the
   * iteration ran". Surfaced so a run matrix can show "ran" instead of a green
   * pass (a "Testing" case scored a pass on claude-desktop, 2026-08-22).
   */
  ungraded?: boolean;
  matcher: MatcherResult | null;
  judge?: JudgeVerdict;
  error?: string;
  /** Failure classification (env/infra/model): stamped on error-path grades by
   * `failIteration`, and on complete-but-failed grades by `gradeIteration` when
   * the transcript shows an env denial (credit exhaustion / 402). */
  kind?: FailureKind;
  /** Server-side truth for the iteration's time window (M5); absent when no Yarn truth source is configured. */
  correlation?: ServerTruth;
  /** Post-run reads of the projects this iteration's video-agent runs touched; absent when nothing was dispatched. */
  outcome?: OutcomeReport;
}

// ---- run event stream ----
export type SphinxEvent =
  | { type: "run_start"; runId: string }
  | { type: "queue_position"; iterationId: string; position: number }
  | { type: "iteration_start"; iterationId: string; target: TargetId; model: SphinxModel }
  | { type: "text_delta"; iterationId: string; text: string }
  | { type: "tool_call"; iterationId: string; name: string; args?: unknown; serverRecorded?: boolean; server?: string; sKey?: string }
  | { type: "tool_result"; iterationId: string; name: string; summary: string; isError?: boolean; serverRecorded?: boolean; server?: string }
  | { type: "iteration_finish"; iterationId: string; status: IterationStatus }
  | { type: "grade"; iterationId: string; grade: Grade }
  | { type: "error"; iterationId?: string; message: string }
  /** Non-fatal operational note ("orphan requeued for one retry"); readers may ignore it. */
  | { type: "log"; iterationId?: string; message: string }
  | { type: "complete"; runId: string; summary: { total: number; passed: number; failed: number; env?: number; infra?: number; model?: number } }
  /** Operator cancelled the run; `settleCanceledRun` then stops the Yarn agents and restores. */
  | { type: "canceled"; runId: string };

// ---- runner wire protocol (Mac runner <-> server over one WebSocket) ----
// One JSON object per WS text frame (NDJSON-style: no framing beyond the
// frame itself). Runner dials `GET /api/sphinx/runner`, sends `register`
// first; the server answers `registered` or `reject` and then pushes `assign`
// / `cancel`. Everything below is validated with zod on both ends.
export const RUNNER_SLOT_KINDS = ["ui-exclusive", "cdp"] as const;
export type RunnerSlotKind = (typeof RUNNER_SLOT_KINDS)[number];
export const SLOT_FOR_CLASS: Record<Exclude<JobClass, "server">, RunnerSlotKind> = {
  "mac-exclusive": "ui-exclusive",
  "mac-cdp": "cdp",
};

export const runnerSlotsSchema = z.object({
  "ui-exclusive": z.number().int().min(0).optional(),
  cdp: z.number().int().min(0).optional(),
});
export type RunnerSlots = z.infer<typeof runnerSlotsSchema>;

// Loose structural schemas for payloads whose canonical shape is a TS type above.
const observedToolCallSchema = z.object({
  name: z.string(),
  args: z.unknown().optional(),
  resultSummary: z.string().optional(),
  isError: z.boolean().optional(),
  /** Which MCP server served the call (connector name) — a multi-connector run
   * calls the same tool once per org, and unlabeled they read as duplicates. */
  server: z.string().optional(),
});
export const clientTranscriptSchema = z.object({
  turns: z.array(
    z.object({
      prompt: z.string(),
      responseText: z.string(),
      observedToolCalls: z.array(observedToolCallSchema),
    })
  ),
  exactness: z.enum(["exact", "scraped"]),
  raw: z.unknown().optional(),
});
export const sphinxEventSchema = z.looseObject({ type: z.string().min(1) });

export const JOB_OUTCOMES = ["succeeded", "failed", "canceled"] as const;
export type JobOutcome = (typeof JOB_OUTCOMES)[number];

// runner -> server
export const registerMessageSchema = z.object({
  type: z.literal("register"),
  runnerId: z.string().min(1),
  name: z.string().min(1),
  token: z.string().min(1),
  capabilities: z.array(z.string().min(1)),
  slots: runnerSlotsSchema,
});
export const heartbeatMessageSchema = z.object({
  type: z.literal("heartbeat"),
  runnerId: z.string().min(1),
  jobIds: z.array(z.string().min(1)),
});
export const eventMessageSchema = z.object({
  type: z.literal("event"),
  jobId: z.string().min(1),
  event: sphinxEventSchema,
});
export const jobDoneMessageSchema = z.object({
  type: z.literal("job_done"),
  jobId: z.string().min(1),
  outcome: z.enum(JOB_OUTCOMES),
  transcript: clientTranscriptSchema.optional(),
  error: z.string().optional(),
  artifactPrefix: z.string().optional(),
});
export const orphanReportMessageSchema = z.object({
  type: z.literal("orphan_report"),
  jobId: z.string().min(1),
  reason: z.string().min(1),
});
export const runnerToServerSchema = z.discriminatedUnion("type", [
  registerMessageSchema,
  heartbeatMessageSchema,
  eventMessageSchema,
  jobDoneMessageSchema,
  orphanReportMessageSchema,
]);
export type RegisterMessage = z.infer<typeof registerMessageSchema>;
export type HeartbeatMessage = z.infer<typeof heartbeatMessageSchema>;
export type EventMessage = z.infer<typeof eventMessageSchema>;
export type JobDoneMessage = z.infer<typeof jobDoneMessageSchema>;
export type OrphanReportMessage = z.infer<typeof orphanReportMessageSchema>;
export type RunnerToServer = z.infer<typeof runnerToServerSchema>;

// server -> runner
export const uploadUrlSchema = z.object({ key: z.string().min(1), url: z.string().url() });
export type UploadUrl = z.infer<typeof uploadUrlSchema>;
export const registeredMessageSchema = z.object({
  type: z.literal("registered"),
  runnerId: z.string().min(1),
});
export const assignMessageSchema = z.object({
  type: z.literal("assign"),
  job: jobSpecSchema,
  uploadUrls: z.array(uploadUrlSchema),
});
export const cancelMessageSchema = z.object({
  type: z.literal("cancel"),
  jobId: z.string().min(1),
});
export const rejectMessageSchema = z.object({
  type: z.literal("reject"),
  reason: z.string().min(1),
});
/**
 * Server's receipt for a `job_done`: sent after the report is processed (applied
 * or not), so the runner can drop its persisted copy. Finishes are at-least-once:
 * the runner keeps the frame in its on-disk book and re-sends it on every
 * re-register until acked — a deploy severing the socket mid-`job_done` used to
 * lose the finish outright and the sweep failed the iteration with no reason
 * (2026-08-25).
 */
export const jobAckMessageSchema = z.object({
  type: z.literal("job_ack"),
  jobId: z.string().min(1),
});
export const serverToRunnerSchema = z.discriminatedUnion("type", [
  registeredMessageSchema,
  assignMessageSchema,
  cancelMessageSchema,
  rejectMessageSchema,
  jobAckMessageSchema,
]);
export type AssignMessage = z.infer<typeof assignMessageSchema>;
export type ServerToRunner = z.infer<typeof serverToRunnerSchema>;

// ---- server-side truth (grading/yarn-truth.ts, grading/correlate.ts) ----
export const SERVER_TRUTH_SOURCES = ["db", "s3"] as const;
export type ServerTruthSource = (typeof SERVER_TRUTH_SOURCES)[number];

/**
 * One normalized event from Yarn's side. `s3` events are per MCP HTTP exchange
 * (`mcp-logs/<env>/...` archive: method + toolName for tools/call); `db` events
 * are one per `mcp_session` row active in the window (method "session", no tool
 * name — the table counts calls, it does not name them).
 */
export interface ServerTruthEvent {
  ts: string;
  sessionId: string | null;
  method: string;
  toolName?: string;
  isError?: boolean;
  /** tools/call only: the result's text content (or the RPC error), truncated — enough to render the call, never the full body. */
  resultSummary?: string;
  durationMs?: number;
  /** clientInfo.name from the session's initialize / server/discover (S3 source only). */
  clientName?: string;
  source: ServerTruthSource;
  /** Small, joinable leftovers (S3 key, rpc id, args, http status / session counters) — never request or response bodies. */
  raw?: unknown;
}

export interface ServerTruth {
  windowStart: string;
  windowEnd: string;
  sources: ServerTruthSource[];
  events: ServerTruthEvent[];
  /** tools/call names seen server-side in the window (S3 source only), in order. */
  observedToolNames: string[];
  /** Other iterations on the same connector genuinely running at the same time: actual [started_at, finished_at] intervals intersect (the window's padding does not count). */
  overlapping: { iterationId: string; target: TargetId }[];
  /**
   * The subset of `overlapping` whose client names this iteration's client
   * filter cannot tell apart from its own (e.g. Claude Desktop ∥ Cowork —
   * both are Claude.app). Server-side tool attribution is unreliable for the
   * window, so the matcher grades from the transcript instead; deliberate
   * same-connector parallelism is NOT marked suspect for this.
   */
  ambiguousOverlaps?: { iterationId: string; target: TargetId }[];
  /**
   * Sessions in the window attributed by clientInfo.name: only sessions whose client
   * matches the target under test count (a shared Yarn org sees other humans/clients).
   * Sessions that never named themselves in the window are kept but counted here.
   */
  clientFilter?: { expected: string[]; keptSessions: number; droppedSessions: number; unattributedSessions: number; droppedClients: string[] };
  /**
   * Other Yarn orgs the job co-enabled (`spec.servers` carried their connectors).
   * The graded org's record is a PARTIAL view then: transcript calls that landed
   * in a co-enabled org are expected and not marked suspect.
   */
  coEnabledOrgs?: string[];
  /**
   * Server-side tools/call names the transcript lacks, when concurrent
   * exact-transcript (server-class) iterations on the connector explain them:
   * a mixed run's API/CLI iterations flood the org record and the window
   * cannot split long-lived unattributed sessions. Reported, not suspect —
   * and while present the matcher grades from the transcript, never from
   * server truth (a foreign run_project_agent — or pre-consolidation run_video_agent — must not pass someone else's case).
   */
  serverExtras?: { names: string[]; explainedBy: { iterationId: string; target: TargetId }[] };
  /**
   * The archive read exceeded its object cap; `events` are a prefix of the
   * window. Reported, never suspect — the matcher grades from the transcript
   * instead of an incomplete record.
   */
  truncated?: boolean;
  suspect: boolean;
  suspectReason?: string;
}

// ---- queue routes (server/routes/sphinx/queue.ts) ----
export const jobStateSchema = z.enum(JOB_STATES);
export const LIVE_JOB_STATES = ["queued", "assigned", "running"] as const satisfies readonly JobState[];
export const isLiveJobState = (s: string): s is (typeof LIVE_JOB_STATES)[number] =>
  (LIVE_JOB_STATES as readonly string[]).includes(s);
/** `GET /jobs?state=a,b&limit=n` after the query string is split. */
export const listJobsQuerySchema = z.object({
  state: z.array(jobStateSchema).min(1).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(200),
});
/** Scheduler order is priority desc, then FIFO; the UI bumps within this band. */
export const setJobPrioritySchema = z.object({
  priority: z.number().int().min(-100).max(100),
});
